import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontalIcon } from 'lucide-react';

interface TripApprovalActionsProps {
  layout?: 'standalone' | 'inline';
  onBack: () => void;
  onApprove: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  showApprove?: boolean;
  showReject?: boolean;
  showEdit?: boolean;
  isApproving?: boolean;
  isRejecting?: boolean;
  isEditing?: boolean;
  disableApprove?: boolean;
  disableReject?: boolean;
  disableEdit?: boolean;
  className?: string;
}

export function TripApprovalActions({
  layout = 'standalone',
  onBack,
  onApprove,
  onReject,
  onEdit,
  showApprove = true,
  showReject = true,
  showEdit = true,
  isApproving = false,
  isRejecting = false,
  isEditing = false,
  disableApprove = false,
  disableReject = false,
  disableEdit = false,
  className,
}: TripApprovalActionsProps) {
  const tNav = useTranslations('navigation');
  const tActions = useTranslations('trips.actions');

  const isStandalone = layout === 'standalone';

  const additionalActions = [];

  if (showReject && onReject) {
    additionalActions.push({
      id: 'reject',
      label: tActions('reject'),
      onSelect: onReject,
      disabled: disableReject || isRejecting,
      isLoading: isRejecting,
      tone: 'destructive' as const,
    });
  }

  if (showEdit && onEdit) {
    additionalActions.push({
      id: 'edit',
      label: tActions('edit'),
      onSelect: onEdit,
      disabled: disableEdit || isEditing,
      isLoading: isEditing,
      tone: 'default' as const,
    });
  }

  const overflowAriaLabel =
    additionalActions.length === 1
      ? additionalActions[0]?.label
      : additionalActions.length > 1
        ? additionalActions.map((action) => action.label).join(', ')
        : undefined;

  const containerClasses = cn(
    'w-full overflow-x-hidden px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]',
    isStandalone ? 'border-t border-[#f1f1f1] bg-white' : '',
    className
  );

  return (
    <div className={containerClasses}>
      <div className="hidden w-full items-center justify-between lg:flex">
        <Button
          variant="ghost"
          onClick={onBack}
          className="flex h-12 items-center gap-1.5 rounded-full px-4 text-base font-medium text-black hover:bg-[#f9f9f9]"
        >
          <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5 opacity-80" />
          {tNav('back')}
        </Button>

        <div className="flex items-center gap-4">
          {showReject && (
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={disableReject || isRejecting}
              className="flex h-12 items-center gap-2 rounded-full px-6 py-3 text-base font-medium text-white transition-transform hover:scale-[0.99] disabled:opacity-50"
              style={{ backgroundColor: '#E5484D' }}
            >
              {isRejecting ? (
                <Spinner variant="circle" className="text-white" size={16} strokeWidth={2} />
              ) : (
                <img src="/assets/icons/x-white.svg" alt="" className="mt-0.5 h-6 w-6" />
              )}
              {tActions('reject')}
            </Button>
          )}

          {showEdit && (
            <Button
              variant="default"
              onClick={onEdit}
              disabled={disableEdit || isEditing}
              className="flex h-12 items-center gap-2 rounded-full px-6 py-3 text-base font-medium text-white transition-transform hover:scale-[0.99] disabled:opacity-50"
              style={{ backgroundColor: '#171716' }}
            >
              {isEditing ? (
                <Spinner variant="circle" className="text-white" size={16} strokeWidth={2} />
              ) : (
                <img
                  src="/assets/icons/edit-hollow.svg"
                  alt=""
                  className="h-[22px] w-[22px] fill-current text-white"
                />
              )}
              {tActions('edit')}
            </Button>
          )}

          {showApprove && (
            <Button
              variant="brand"
              onClick={onApprove}
              disabled={disableApprove || isApproving}
              className="flex h-12 items-center gap-2 rounded-full px-6 py-3 text-base font-medium text-white transition-transform hover:scale-[0.99] disabled:opacity-50"
            >
              {isApproving ? (
                <Spinner variant="circle" className="text-white" size={16} strokeWidth={2} />
              ) : (
                <img src="/assets/icons/file-check.svg" alt="" className="h-5 w-5" />
              )}
              {tActions('approve')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex w-full min-w-0 items-center justify-between gap-2 lg:hidden">
        <Button
          variant="ghost"
          onClick={onBack}
          className="flex h-10 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-black hover:bg-[#f1f1f1]"
        >
          <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5 opacity-80" />
          {tNav('back')}
        </Button>

        <div className="flex min-w-0 items-center justify-end gap-2">
          {additionalActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e4e4e4] bg-white text-[#292929] transition-colors hover:bg-[#f1f1f1]"
                  aria-label={overflowAriaLabel}
                >
                  <MoreHorizontalIcon className="h-5 w-5" aria-hidden />
                  {overflowAriaLabel ? <span className="sr-only">{overflowAriaLabel}</span> : null}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-[14rem] rounded-2xl border border-[#ededed] bg-white p-1 shadow-[0px_12px_32px_-12px_rgba(31,31,31,0.12)]"
              >
                {additionalActions.map((action) => (
                  <DropdownMenuItem
                    key={action.id}
                    onSelect={(event) => {
                      event.preventDefault();
                      if (action.disabled) {
                        return;
                      }
                      action.onSelect?.();
                    }}
                    disabled={action.disabled}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm leading-5 focus:bg-[#f9f9f9] data-[highlighted]:bg-[#f9f9f9]',
                      action.tone === 'destructive' ? 'text-[#e5484d]' : 'text-[#292929]'
                    )}
                  >
                    <span>{action.label}</span>
                    {action.isLoading ? (
                      <Spinner
                        variant="circle"
                        className="text-[#bbbbbb]"
                        size={16}
                        strokeWidth={2}
                      />
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {showApprove && (
            <Button
              variant="brand"
              onClick={onApprove}
              disabled={disableApprove || isApproving}
              className="flex h-11 items-center gap-2 rounded-full px-5 text-sm font-medium text-white transition-transform hover:scale-[0.99] disabled:opacity-50"
            >
              {isApproving ? (
                <Spinner variant="circle" className="text-white" size={16} strokeWidth={2} />
              ) : (
                <img src="/assets/icons/file-check.svg" alt="" className="h-5 w-5" />
              )}
              {tActions('approve')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TripApprovalActions;
